// @ts-nocheck — mixin methods are composed onto BackendService at runtime; instance API is typed via adminBackendServiceInterface.ts.
import type BaseBackendService from '../BaseBackendService';
import { BackgroundJob } from 'BackgroundJob';
import ContentManagementChatResponse from 'ContentManagementChatResponse';
import { DocumentStatus } from 'DocumentStatus';
import FundOpportunityChatResponse from 'FundOpportunityChatResponse';
import AIChatMessage from '../../models/AIChatMessage';

export const adminBackendServiceContentGenerationMixin = {
  generateContentBlock(type: string, prompt: string): Promise<any> {
    return this.authApiMutateAuthed('generateContent', 'POST', { type, prompt }, (json) => json);
  },

  refineContentBlock(block: any, type: string, prompt: string): Promise<any> {
    return this.authApiMutateAuthed(
      'refineContent',
      'POST',
      { block, type, prompt },
      (json) => json,
    );
  },

  generateFundOpportunity(
    options: {
      inputData?: string;
      currentData?: FundOpportunityChatResponse['currentData'];
      history?: AIChatMessage[];
      fieldBeingAskedFor?: keyof FundOpportunityChatResponse['currentData'] | null;
      uploadedDocuments?: any[];
    } = {},
  ): Promise<FundOpportunityChatResponse> {
    const { inputData, currentData, history, fieldBeingAskedFor, uploadedDocuments } = options;
    const body: Record<string, any> = {};
    if (inputData) body.inputData = inputData;
    if (currentData) body.currentData = currentData;
    if (history) body.history = history;
    if (fieldBeingAskedFor) body.fieldBeingAskedFor = fieldBeingAskedFor;
    if (uploadedDocuments) body.uploadedDocuments = uploadedDocuments;

    return this.authApiMutateAuthed(
      'generateFundOpportunity',
      'POST',
      body,
      (json) => json.data as FundOpportunityChatResponse,
    );
  },

  getUploadUrl(
    tempFundOpportunityId: string,
    fileName: string,
    mimeType?: string,
    fileSize?: number,
  ): Promise<{
    uploadUrl: string;
    documentId: string;
    gcsPath: string;
    expiresAt: string;
  }> {
    return this.authApiMutateAuthed(
      'fundOpportunities/getUploadUrl',
      'POST',
      { tempFundOpportunityId, fileName, mimeType, fileSize },
      (json) => json.data,
    );
  },

  uploadToGCS(
    uploadUrl: string,
    file: File,
    onUploadProgress?: (progress: number) => void,
  ): Promise<void> {
    return this.putFileViaXhr(
      uploadUrl,
      file,
      file.type || 'application/octet-stream',
      onUploadProgress,
    );
  },

  completeUpload(
    tempFundOpportunityId: string,
    documentId: string,
    gcsPath: string,
  ): Promise<{
    document: {
      id: string;
      originalFileName: string;
      geminiDocumentUrl: string;
      mimeType: string;
      uploadedAt: string;
      gcsPath: string;
    };
    tempFundOpportunityId: string;
  }> {
    return this.authApiMutateAuthed(
      'fundOpportunities/completeUpload',
      'POST',
      { tempFundOpportunityId, documentId, gcsPath },
      (json) => json.data,
    );
  },

  uploadDocument(
    file: File,
    tempFundOpportunityId: string,
    onUploadProgress?: (progress: number) => void,
  ): Promise<{
    document: {
      id: string;
      originalFileName: string;
      geminiDocumentUrl: string;
      mimeType: string;
      uploadedAt: string;
      gcsPath: string;
    };
    tempFundOpportunityId: string;
  }> {
    return this.authFetchPromise(async (token) => {
      const formData = new FormData();
      formData.append('document', file);
      formData.append('tempFundOpportunityId', tempFundOpportunityId);

      return this.xhrUpload('POST', this.apiURL('fundOpportunities/uploadDocument'), formData, {
        headers: { Authorization: `Bearer ${token}` },
        onUploadProgress,
        mapResponse: (xhr) => JSON.parse(xhr.responseText).data,
      });
    });
  },

  processUploadedDocuments(
    tempFundOpportunityId: string,
    currentData: any = {},
    history: AIChatMessage[] = [],
  ): Promise<{
    jobId: string;
    jobExternalId: string;
    status: string;
    progressText: string;
    message: string;
  }> {
    return this.authApiMutateAuthed(
      'fundOpportunities/processDocuments',
      'POST',
      { tempFundOpportunityId, currentData, history },
      (json) => json.data,
    );
  },

  getJobProgress(jobId: string): Promise<BackgroundJob> {
    return this.authApiGetAuthed(
      `jobs/${jobId}`,
      'Network error while fetching job progress',
      (json) => json.data as BackgroundJob,
    );
  },

  uploadAndProcessDocument(
    file: File,
    currentData: any = {},
    history: AIChatMessage[] = [],
  ): Promise<FundOpportunityChatResponse> {
    return this.authFetchPromise(async (token) => {
      const formData = new FormData();
      formData.append('document', file);
      formData.append('currentData', JSON.stringify(currentData));
      formData.append('history', JSON.stringify(history));

      const response = await this.authenticatedFetch(
        this.apiURL('fundOpportunities/uploadDocument'),
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        },
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw errorData.error || `Error: ${response.status}`;
      }
      const data = await response.json();
      return data.data as FundOpportunityChatResponse;
    });
  },

  generateFromDocument(
    documentUrl: string,
    mimeType: string,
    originalFileName: string,
    currentData: any = {},
    history: AIChatMessage[] = [],
  ): Promise<FundOpportunityChatResponse> {
    return this.authApiMutateAuthed(
      'fundOpportunities/generateFromDocument',
      'POST',
      { documentUrl, mimeType, originalFileName, currentData, history },
      (json) => json.data as FundOpportunityChatResponse,
    );
  },

  generateContentManagementFromDocuments(
    fundOpportunityId: string,
    documentUrls: string[],
    currentContent: any[] = [],
    history: AIChatMessage[] = [],
    userPrompt?: string,
  ): Promise<ContentManagementChatResponse> {
    return this.authApiMutateAuthed(
      'contentManagement/generateFromDocuments',
      'POST',
      { documentUrls, fundOpportunityId, currentContent, history, userPrompt },
      (json) => json.data as ContentManagementChatResponse,
    );
  },

  refineContentManagement(
    currentContent: any[],
    userPrompt: string,
    documentUrls: string[] = [],
    history: AIChatMessage[] = [],
  ): Promise<ContentManagementChatResponse> {
    return this.authApiMutateAuthed(
      'contentManagement/refine',
      'POST',
      { currentContent, userPrompt, documentUrls, history },
      (json) => json.data as ContentManagementChatResponse,
    );
  },

  generateContentWithUploadsOrUrls(
    fundOpportunityId: string,
    files: File[] = [],
    documentUrls: {
      url?: string;
      encryptedDocumentUrl?: string;
      mimeType: string;
      layoutData?: any;
    }[] = [],
    extraInfo: string = '',
    aiInstructions: string = '',
  ): Promise<{ jobId: string }> {
    return this.authFetchPromise(async (token) => {
      const formData = new FormData();
      files.forEach((file) => formData.append('documents', file));
      formData.append('fundOpportunityId', fundOpportunityId);
      formData.append('extraInfo', extraInfo);
      formData.append('aiInstructions', aiInstructions);
      if (documentUrls.length > 0) {
        formData.append('documentUrls', JSON.stringify(documentUrls));
      }

      const response = await this.authenticatedFetch(
        this.apiURL('contentManagement/generateContent'),
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        },
      );
      if (!response.ok) {
        throw await response.json();
      }
      const json = await response.json();
      return json.data as { jobId: string };
    });
  },

  getDocumentsForContentGeneration(fundOpportunityId: string): Promise<
    Array<{
      location: string;
      encryptedDocumentUrl: string;
      originalFileName: string;
      mimeType: string;
    }>
  > {
    return this.authApiGetAuthed(
      `fundOpportunities/${fundOpportunityId}/documentsForContentGeneration`,
      'Network error while fetching documents for content generation',
      (json) => json.data || [],
    );
  },

  checkDocumentStatus(
    documents: {
      name: string;
      location?: string;
      originalFileName?: string;
      timestamp: string;
    }[],
  ): Promise<{
    documents: DocumentStatus[];
  }> {
    return this.authApiMutateAuthed(
      'contentManagement/checkDocumentStatus',
      'POST',
      { documents },
      (json) => json,
    );
  },
} satisfies ThisType<BaseBackendService>;
